import React, { useEffect, useState } from 'react'
import styled from 'styled-components/macro'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { t } from 'ttag'
import 'rc-slider/assets/index.css'
import 'rc-tooltip/assets/bootstrap.css'
import { updateSelectedFilters } from 'store/actions/filters'
import FilterTitle from 'components/ui/FilterTitle'
import SearchFilter from 'components/ui/SearchFilter'
import SelectedFilter from 'components/ui/SelectedFilter'
import CustomCheckbox from 'components/ui/CustomCheckbox'
import FilterText from 'components/ui/FilterText'
import RemoveIcon from 'components/ui/RemoveIcon'
import SelectedFilterContainer from 'components/ui/SelectedFilterContainer'
import { SELECTED_FILTER_MAX_LINES } from 'store/constants/constants'
import Dotdotdot from 'react-dotdotdot'

const FilterSection = styled.div`
  margin-bottom: 0px;
`

const SubHeader = styled.h4`
  font-size: 16px;
  font-weight: normal;
  margin-bottom: 10px;
  font-family: 'Open Sans';
`

const Input = styled.input`
  margin-right: 5px;
`

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #4a4a4a;
`

const CustomSearchText = styled(FilterText)`
  font-size: 1.2rem;
  margin-bottom: 10px;
`
const SearchErrorMessage = styled(FilterText)`
  font-size: 0.8rem;
  color: red;
  margin-bottom: 5px;
  font-weight: bold;
`

const SearchInput = styled.input`
  color: #ffffff;
  font-size: 1rem;
  line-height: 20px;
  text-align: left;
  border-radius: 4px;
  background-color: #fff;
  padding: 12px 40px;
  border: 0;
  margin: 0 0 0 0;
  width: 100%;
  transition: all 0.2s;
  cursor: pointer;
  border: 1px solid #9faee5;
  color: #4a4a4a;
  padding: 7px 10px;
  padding-right: 35px;
  height: 40px;
  margin-bottom: 10px;

  &:focus {
    background-color: #fff;
    border: 1px solid #9faee5;
    box-shadow: 0 0 6px 0 #003399;
    color: #4a4a4a;
  }
  &:active {
    background-color: #fff;
    border: 1px solid #9faee5;
    box-shadow: 0 0 6px 0 #003399;
    color: #4a4a4a;
  }

  &:last-child {
    margin-right: 0;
  }

  :disabled {
    background: gray;
  }

  &:focus {
    outline: none;
  }
`

function ProjectBudget({ type, filterData, values, title, showFilter, customTop, selection }) {
  const filters = useSelector(state => state.filters)
  const { currentSearchable } = filters
  const filterObject = filters.selectedFilters[currentSearchable][type]

  return (
    <PureProjectBudget
      type={type}
      values={values}
      filterData={filterData}
      title={title}
      showFilter={showFilter}
      customTop={customTop}
      filterObject={filterObject}
      selection={selection}
    />
  )
}

const PureProjectBudget = React.memo(function PureProjectBudget({
  type,
  title,
  values,
  showFilter,
  customTop,
  filterObject,
  selection
}) {
  // Initialize budgetRange with default values
  const [budgetRange, setBudgetRange] = useState({
    min_budget: 0,
    max_budget: 1000000
  })

  // Initialize selected max and min budgets
  const [selectedMinBudget, setSelectedMinBudget] = useState(budgetRange.min_budget)
  const [selectedMaxBudget, setSelectedMaxBudget] = useState(budgetRange.max_budget)

  // Validdation status of budget inputs
  const [selectedMinBudgetValid, setSelectedMinBudgetValid] = useState(true)
  const [selectedMaxBudgetValid, setSelectedMaxBudgetValid] = useState(true)

  function numberFormat(toFormat, decimals, decPoint, thousandsSep) {
    // Strip all characters but numerical ones.
    let toFormatCopy = toFormat
    toFormatCopy = `${toFormatCopy}`.replace(/[^0-9+\-Ee.]/g, '')
    const n = !Number.isFinite(+toFormatCopy) ? 0 : +toFormatCopy
    const prec = !Number.isFinite(+decimals) ? 0 : Math.abs(decimals)
    const sep = typeof thousandsSep === 'undefined' ? ',' : thousandsSep
    const dec = typeof decPoint === 'undefined' ? '.' : decPoint
    let s = ''
    const toFixedFix = (nn, precc) => {
      const k = 10 ** precc
      return `${Math.round(nn * k) / k}`
    }
    // Fix for IE parseFloat(0.55).toFixed(0) = 0;
    s = (prec ? toFixedFix(n, prec) : `${Math.round(n)}`).split('.')
    if (s[0].length > 3) {
      s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep)
    }
    if ((s[1] || '').length < prec) {
      s[1] = s[1] || ''
      s[1] += new Array(prec - s[1].length + 1).join('0')
    }
    return s.join(dec)
  }

  // Get the max and min budgets from database
  useEffect(() => {
    async function getBudgetRange() {
      const response = await fetch('/api/search/projects/budget-range/')
      const data = await response.json()

      // Set the budget range to values from the database
      setBudgetRange({
        min_budget: data.min_budget,
        max_budget: data.max_budget
      })
    }

    getBudgetRange()
  }, [])

  useEffect(() => {
    // Set selected min and max budgets to the new values
    setSelectedMinBudget(numberFormat(budgetRange.min_budget, 0, '.', ' '))
    setSelectedMaxBudget(numberFormat(budgetRange.max_budget, 0, '.', ' '))
  }, [budgetRange])

  let checkboxWithoutBudget = filterObject.project_budget.without_budget
  if (!checkboxWithoutBudget) checkboxWithoutBudget = false

  const dispatch = useDispatch()

  function renderSelectedMinBudgetError() {
    if (selectedMinBudgetValid) return <></>
    return <SearchErrorMessage>Please enter a valid min budget!</SearchErrorMessage>
  }

  function renderSelectedMaxBudgetError() {
    if (selectedMaxBudgetValid) return <></>
    return <SearchErrorMessage>Please enter a valid max budget!</SearchErrorMessage>
  }

  function validateInput(value) {
    const parsedValue = value.replace(/\s/g, '')
    let valid = true

    if (isNaN(parsedValue) || isNaN(parseInt(parsedValue))) valid = false // eslint-disable-line
    if (parsedValue < budgetRange.min_budget || parsedValue > budgetRange.max_budget) valid = false

    return { parsedValue: parseInt(parsedValue, 10), valid }
  }

  function handleSelectedMinBudgetChange(e) {
    // Validate input
    let { parsedValue, valid } = validateInput(e.target.value) // eslint-disable-line
    if (parsedValue > selectedMaxBudget) valid = false

    // Set min budget validation status
    setSelectedMinBudgetValid(valid)

    const formatedValue = numberFormat(parsedValue, 0, '.', ' ')

    if (valid) {
      // Update the value
      setSelectedMinBudget(formatedValue)

      // Update the filter
      const projectBudget = {
        range: [formatedValue.replace(/\s/g, ''), selectedMaxBudget.replace(/\s/g, '')],
        without_budget: checkboxWithoutBudget
      }

      const data = {
        filterType: type,
        filterId: 'project_budget',
        filterValue: projectBudget
      }

      dispatch(updateSelectedFilters(data))
    } else {
      setSelectedMinBudget(e.target.value)
    }
  }

  function handleSelectedMaxBudgetChange(e) {
    // Validate input
    let { parsedValue, valid } = validateInput(e.target.value) // eslint-disable-line
    if (parsedValue < selectedMinBudget) valid = false

    // Set max budget validation status
    setSelectedMaxBudgetValid(valid)

    const formatedValue = numberFormat(parsedValue, 0, '.', ' ')

    if (valid) {
      // Update the value
      setSelectedMaxBudget(formatedValue)

      // Update the filter
      const projectBudget = {
        range: [selectedMinBudget.replace(/\s/g, ''), formatedValue.replace(/\s/g, '')],
        without_budget: checkboxWithoutBudget
      }

      const data = {
        filterType: type,
        filterId: 'project_budget',
        filterValue: projectBudget
      }

      dispatch(updateSelectedFilters(data))
    } else {
      setSelectedMaxBudget(e.target.value)
    }
  }

  function handleRangeFilterRemove() {
    // Reset selected budgets
    setSelectedMinBudget(numberFormat(budgetRange.min_budget, 0, '.', ' '))
    setSelectedMaxBudget(numberFormat(budgetRange.max_budget, 0, '.', ' '))

    const resultingValue = {
      range: [],
      without_budget: checkboxWithoutBudget
    }

    const data = {
      filterType: type,
      filterId: 'project_budget',
      filterValue: resultingValue
    }

    dispatch(updateSelectedFilters(data))
  }

  function handleCheckboxWithoutBudgetChange() {
    const withoutBudgetChecked = !checkboxWithoutBudget

    const projectBudget = {
      range: [selectedMinBudget.replace(/\s/g, ''), selectedMaxBudget.replace(/\s/g, '')],
      without_budget: withoutBudgetChecked
    }

    const data = {
      filterType: type,
      filterId: 'project_budget',
      filterValue: projectBudget
    }

    dispatch(updateSelectedFilters(data))
  }

  function handleCheckboxWithoutBudgetRemove() {
    // Reset the checkbox
    checkboxWithoutBudget = false

    const resultingValue = {
      range: [selectedMinBudget.replace(/\s/g, ''), selectedMaxBudget.replace(/\s/g, '')],
      without_budget: false
    }

    const data = {
      filterType: type,
      filterId: 'project_budget',
      filterValue: resultingValue
    }

    dispatch(updateSelectedFilters(data))
  }

  function renderSelectedValues() {
    return values.map(value => {
      if (filterObject.project_budget) {
        if (value === 'project_budget.range') {
          const filterValue = filterObject.project_budget.range
          if (
            typeof filterValue[0] === 'undefined' ||
            typeof filterValue[1] === 'undefined' ||
            (filterValue[0] === budgetRange.min_budget && filterValue[1] === budgetRange.max_budget)
          )
            return false

          const displayMinBudget = numberFormat(filterValue[0], 0, '.', ' ')
          const displayMaxBudget = numberFormat(filterValue[1], 0, '.', ' ')

          const selectedFilterText = `Budget range: EUR ${displayMinBudget} to EUR ${displayMaxBudget}`

          return (
            <SelectedFilterContainer className="selected-filters" key={filterValue}>
              <SelectedFilter isCapitalized>
                <FilterText title={selectedFilterText}>
                  <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{selectedFilterText}</Dotdotdot>
                </FilterText>
                <RemoveIcon className="far fa-times" onClick={() => handleRangeFilterRemove()} />
              </SelectedFilter>
            </SelectedFilterContainer>
          )
        } else if (value === 'project_budget.without_budget') {
          const filterValue = filterObject.project_budget.without_budget
          if (!filterValue) return false

          const selectedFilterText = 'Include projects without known total budget'

          return (
            <SelectedFilterContainer className="selected-filters" key={filterValue}>
              <SelectedFilter isCapitalized>
                <FilterText title={selectedFilterText}>
                  <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{selectedFilterText}</Dotdotdot>
                </FilterText>
                <RemoveIcon
                  className="far fa-times"
                  onClick={() => handleCheckboxWithoutBudgetRemove()}
                />
              </SelectedFilter>
            </SelectedFilterContainer>
          )
        }

        return false
      }
      return false
    })
  }

  const displayMinBudget = numberFormat(budgetRange.min_budget, 0, '.', ' ')
  const displayMaxBudget = numberFormat(budgetRange.max_budget, 0, '.', ' ')

  return (
    <>
      {!selection ? (
        <SearchFilter open={showFilter} customTop={customTop}>
          <div className="sticky-container">
            {title ? <FilterTitle>{title}</FilterTitle> : null}
            <FilterSection>
              <SubHeader>{t`Budget range: EUR ${displayMinBudget} - EUR ${displayMaxBudget}`}</SubHeader>
              <CustomSearchText>Min budget:</CustomSearchText>
              {renderSelectedMinBudgetError()}
              <SearchInput
                value={selectedMinBudget}
                onChange={e => handleSelectedMinBudgetChange(e)}
              />
              <CustomSearchText>Max budget:</CustomSearchText>
              {renderSelectedMaxBudgetError()}
              <SearchInput
                value={selectedMaxBudget}
                onChange={e => handleSelectedMaxBudgetChange(e)}
              />
            </FilterSection>
            <FilterSection>
              <CustomCheckbox>
                <Input
                  type="checkbox"
                  id="without_budget"
                  name="filter-checkbox"
                  value={checkboxWithoutBudget}
                  checked={checkboxWithoutBudget ? 'checked' : ''}
                  onChange={handleCheckboxWithoutBudgetChange}
                />
                <Label htmlFor="without_budget">{t`Include projects without known total budget`}</Label>
              </CustomCheckbox>
            </FilterSection>
          </div>
        </SearchFilter>
      ) : (
        <div className="selected-fl">{renderSelectedValues()}</div>
      )}
    </>
  )
})

ProjectBudget.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.object,
  values: PropTypes.array,
  title: PropTypes.string,
  showFilter: PropTypes.bool,
  customTop: PropTypes.string,
  selection: PropTypes.bool
}

PureProjectBudget.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.object,
  values: PropTypes.array,
  title: PropTypes.string,
  showFilter: PropTypes.bool,
  customTop: PropTypes.string,
  filterObject: PropTypes.object,
  selection: PropTypes.bool
}

export default ProjectBudget
